import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Icon,
    Input,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverTrigger,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { IconAddressBook } from "@tabler/icons-react";
import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { createPatch } from "rfc6902";
import { EntitiesArea } from "../../../../auth";
import { EntityResponse, useAddressBookEntries, useClientEntity, useClientEntityUpdate, useStates } from "../../../../api";
import { useAreaPermissions } from "../../../../hooks";

type EntityContactFormValues = {
    contactName: string;
    contactPhone: string;
    contactStreet1: string;
    contactStreet2: string;
    contactCity: string;
    contactState: string;
    contactZipCode: string;
};

const SectionHeader: FunctionComponent<{ title: string; description: string; action?: ReactNode }> = ({ title, description, action }) => (
    <Box mb={4}>
        <Flex align="flex-start" justify="space-between" gap={4}>
            <Box>
                <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                    {title}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                    {description}
                </Text>
            </Box>
            {action}
        </Flex>
        <Divider mt={3} />
    </Box>
);

export type EntityContactTabProps = {
    clientId: string;
    entityId: string;
};

export const EntityContactTab: FunctionComponent<EntityContactTabProps> = ({ clientId, entityId }) => {
    const entity = useClientEntity(clientId, entityId, {});

    if (!entity.data) {
        return (
            <Flex align="center" justify="center" py={12}>
                <Spinner />
            </Flex>
        );
    }

    return <EntityContactForm clientId={clientId} entity={entity.data} />;
};

const EntityContactForm: FunctionComponent<{ clientId: string; entity: EntityResponse }> = ({ clientId, entity }) => {
    const formValues = useMemo<EntityContactFormValues>(
        () => ({
            contactName: entity.contactName ?? "",
            contactPhone: entity.contactPhone ?? "",
            contactStreet1: entity.contactStreet1 ?? "",
            contactStreet2: entity.contactStreet2 ?? "",
            contactCity: entity.contactCity ?? "",
            contactState: entity.contactState ?? "",
            contactZipCode: entity.contactZipCode ?? "",
        }),
        [entity]
    );

    const {
        setValue,
        reset,
        register,
        handleSubmit,
        formState: { isDirty },
    } = useForm<EntityContactFormValues>();

    useEffect(() => {
        Object.entries(formValues).forEach(([key, value]) => {
            setValue(key as keyof EntityContactFormValues, value);
        });
    }, [formValues, setValue]);

    const areaPermissions = useAreaPermissions(EntitiesArea, clientId);
    const clientEntityUpdate = useClientEntityUpdate();
    const states = useStates();
    const addressBookEntries = useAddressBookEntries();
    const [selectedAddressBookEntryId, setSelectedAddressBookEntryId] = useState("");
    const canUpdate = areaPermissions.has(EntitiesArea.update);
    const selectedAddressBookEntry = addressBookEntries.data?.find(entry => entry.id === selectedAddressBookEntryId);

    const applyAddressBookEntry = () => {
        if (!selectedAddressBookEntry) {
            return;
        }

        const address = selectedAddressBookEntry.address;
        const copiedValues: Pick<
            EntityContactFormValues,
            "contactName" | "contactPhone" | "contactStreet1" | "contactStreet2" | "contactCity" | "contactState" | "contactZipCode"
        > = {
            contactName: selectedAddressBookEntry.name ?? "",
            contactPhone: selectedAddressBookEntry.phone ?? "",
            contactStreet1: address?.street1 ?? "",
            contactStreet2: address?.street2 ?? "",
            contactCity: address?.city ?? "",
            contactState: address?.state ?? "",
            contactZipCode: address?.zipCode ?? "",
        };

        Object.entries(copiedValues).forEach(([key, value]) => {
            setValue(key as keyof EntityContactFormValues, value, { shouldDirty: true });
        });
    };

    const save = async (data: EntityContactFormValues) => {
        const patch = createPatch(formValues, data);
        const response = await clientEntityUpdate.mutateAsync({ clientId, entityId: entity.id, patch });

        reset({
            contactName: response.contactName ?? "",
            contactPhone: response.contactPhone ?? "",
            contactStreet1: response.contactStreet1 ?? "",
            contactStreet2: response.contactStreet2 ?? "",
            contactCity: response.contactCity ?? "",
            contactState: response.contactState ?? "",
            contactZipCode: response.contactZipCode ?? "",
        });
    };

    return (
        <form onSubmit={handleSubmit(save)}>
            <VStack align="stretch" spacing={4}>
                <fieldset disabled={!canUpdate || clientEntityUpdate.isLoading}>
                    <Card variant="withShadow">
                        <CardBody p="1.5rem">
                            <SectionHeader
                                title="Form 8850 Contact"
                                description="The person the state workforce agency should contact about this employer's Form 8850 submissions."
                                action={
                                    <Popover placement="bottom-end" isLazy>
                                        <PopoverTrigger>
                                            <Button size="sm" leftIcon={<Icon as={IconAddressBook} boxSize={4} />} isDisabled={!canUpdate}>
                                                Copy from address book
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent w="320px">
                                            <PopoverArrow />
                                            <PopoverCloseButton />
                                            <PopoverBody>
                                                <VStack align="stretch" spacing={3}>
                                                    {addressBookEntries.isError && (
                                                        <Alert status="error">
                                                            <AlertIcon />
                                                            <AlertDescription>Address book entries could not be loaded.</AlertDescription>
                                                        </Alert>
                                                    )}
                                                    <FormControl isDisabled={addressBookEntries.isLoading || addressBookEntries.isError}>
                                                        <FormLabel fontSize="sm">Address book entry</FormLabel>
                                                        <Select
                                                            size="sm"
                                                            placeholder={addressBookEntries.isLoading ? "Loading address book…" : "Select an entry"}
                                                            value={selectedAddressBookEntryId}
                                                            onChange={event => setSelectedAddressBookEntryId(event.target.value)}
                                                        >
                                                            {(addressBookEntries.data ?? []).map(entry => (
                                                                <option key={entry.id} value={entry.id}>
                                                                    {entry.displayName || entry.name}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <Flex justify="flex-end">
                                                        <Button
                                                            size="sm"
                                                            type="button"
                                                            onClick={applyAddressBookEntry}
                                                            isDisabled={!selectedAddressBookEntry || clientEntityUpdate.isLoading}
                                                        >
                                                            Apply to contact
                                                        </Button>
                                                    </Flex>
                                                </VStack>
                                            </PopoverBody>
                                        </PopoverContent>
                                    </Popover>
                                }
                            />
                            <VStack align="stretch" spacing={4}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <FormControl>
                                        <FormLabel>Contact name</FormLabel>
                                        <Input placeholder="Full name" {...register("contactName")} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Contact phone</FormLabel>
                                        <Input placeholder="(555) 000-0000" {...register("contactPhone")} />
                                    </FormControl>
                                </SimpleGrid>
                                <FormControl>
                                    <FormLabel>Street 1</FormLabel>
                                    <Input {...register("contactStreet1")} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Street 2</FormLabel>
                                    <Input {...register("contactStreet2")} />
                                </FormControl>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <FormControl>
                                        <FormLabel>City</FormLabel>
                                        <Input {...register("contactCity")} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>State</FormLabel>
                                        <Select placeholder="Select state" {...register("contactState")}>
                                            {(states.data ?? []).map(state => (
                                                <option key={state.code} value={state.code}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>ZIP</FormLabel>
                                        <Input {...register("contactZipCode")} />
                                    </FormControl>
                                </SimpleGrid>
                            </VStack>
                        </CardBody>
                    </Card>
                </fieldset>

                <Flex justify="flex-end">
                    <ButtonGroup>
                        <Button variant="ghost" onClick={() => reset(formValues)} isDisabled={!isDirty}>
                            Discard
                        </Button>
                        {canUpdate && (
                            <Button type="submit" colorScheme="blue" isDisabled={!isDirty} isLoading={clientEntityUpdate.isLoading}>
                                Save changes
                            </Button>
                        )}
                    </ButtonGroup>
                </Flex>
            </VStack>
        </form>
    );
};
